import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { createFormHookContexts, useForm } from '@tanstack/react-form';
import isEqual from 'lodash/isEqual';
import { z } from 'zod';

import { isSameSymbol } from '@/entities/symbol';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { ColorPalette } from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import type { AppFormValues, TabSymbol } from '@/features/Dashboard/form/AppForm/context/types';
import getMaxItemsForType from '@/features/Dashboard/form/AppForm/utils/getMaxItemsForType';
import { reorderItems } from '@/features/Dashboard/form/AppForm/utils/reorderItems';
import { AppType } from '@/features/Dashboard/types';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import type { SetState } from '@/types/helpers';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

// Create a Zod schema that matches our AppFormValues union type
const TabSymbolSchema = z.object({
  id: z.string(),
  shortTitle: z.string().min(1, 'Short Title is required'),
  symbol: z.any(),
  group: z.string()
});

const SymbolsStateSchema = z.object({
  active: z.array(TabSymbolSchema).min(1, 'At least one item is required'),
  backlog: z.array(TabSymbolSchema)
});

const ColorPaletteSchema = z.enum(ColorPalette);

// Name validation schema - shared validation for tab names
const NameSchema = createNameSchema('Tab name', TABNAME_MAX_LENGTH);

const FormSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string().optional(),
    type: z.literal(AppType.Spot),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal(AppType.FWD),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal(AppType.Seasonal),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal(AppType.Table),
    name: NameSchema,
    symbols: SymbolsStateSchema
  }),
  z.object({
    type: z.literal(AppType.Tiles),
    symbols: SymbolsStateSchema
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal(AppType.SpotHistoryTable),
    name: NameSchema,
    symbols: SymbolsStateSchema
  })
]);

// Create form and field contexts using TanStack Form's built-in utility
const { formContext, useFormContext: useTanStackFormContext } = createFormHookContexts();

// Additional app-specific context for mode and initialValues management
type AppFormMetaContextValue = {
  setInitialValues: (val: AppFormValues | null) => void;
  mode: 'edit' | 'create';
  setMode: SetState<'edit' | 'create'>;
  resetAppForm: () => void;
  maxItems: number;
  // Symbol operations that integrate with TanStack Form
  addSymbols: (incoming: TabSymbol[], maxActiveCapacity: number) => void;
  removeSymbol: (symbol: TabSymbol) => void;
  updateSymbol: (symbol: TabSymbol) => void;
  canPreview: boolean;
  setCanPreview: SetState<boolean>;
};

const AppFormMetaContext = createContext<AppFormMetaContextValue | undefined>(undefined);

// Internal hook to create form instance - used for type inference
const useCreateAppForm = (initialValues: AppFormValues | null) => {
  return useForm({
    defaultValues: initialValues,
    validators: {
      onChange: FormSchema
    }
  });
};

// Export the form type for use in components
export type AppFormInstance = ReturnType<typeof useCreateAppForm>;

export const AppFormProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<'edit' | 'create'>('create');
  const [maxItems, setMaxItems] = useState<number>(0);
  const [canPreview, setCanPreview] = useState<boolean>(true);

  const form = useCreateAppForm(null as AppFormValues | null);

  const resetAppForm = useCallback(() => {
    form.reset(null);
  }, [form]);

  const setInitialValues = useCallback(
    (values: AppFormValues | null) => {
      // Ensure non-Tiles widgets always carry a stable id from the first render, so
      // toWidget() reuses it across preview/save and downstream state keyed by id
      // (e.g. SpotHistoryTable column widths) survives the save round-trip.
      const withId =
        values && values.type !== AppType.Tiles && !values.id
          ? { ...values, id: generateUniqueUUID(new Set()) }
          : values;
      form.reset(withId);
      form.validate('change');
    },
    [form]
  );

  useEffect(() => {
    let previousType = form.state.values?.type;

    const subscription = form.store.subscribe(state => {
      const currentType = state.values?.type;
      if (previousType !== currentType) {
        setMaxItems(getMaxItemsForType(currentType));
        previousType = currentType;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form]);

  useEffect(() => {
    const current = form.state.values;
    const currentSymbols = current?.symbols;
    if (!currentSymbols) return;

    const newOrder = reorderItems(maxItems, currentSymbols);

    // If nothing changed, do not do anything
    if (isEqual(newOrder, currentSymbols)) return;

    const normalized = { ...current, symbols: newOrder } as typeof current;
    form.reset(normalized);
    form.validate('change');
  }, [form, maxItems]);

  // Symbol operations that work with TanStack Form
  const addSymbols = useCallback(
    (incoming: TabSymbol[], maxActiveCapacity: number) => {
      const current = form.state.values?.symbols;
      if (!current) return;

      // Fill active up to capacity with items not already active
      const free = Math.max(0, maxActiveCapacity - current.active.length);
      const notInActive = incoming.filter(
        item => !current.active.some(s => isSameSymbol(s.symbol, item.symbol))
      );
      const promote = notInActive.slice(0, free);
      const nextActive = promote.length ? [...current.active, ...promote] : current.active;

      // Overflow = incoming not in nextActive
      const overflow = incoming.filter(
        item => !nextActive.some(s => isSameSymbol(s.symbol, item.symbol))
      );

      // Backlog: remove items that moved to active, then append overflow (no dups)
      const kept = current.backlog.filter(
        item => !nextActive.some(s => isSameSymbol(s.symbol, item.symbol))
      );
      const toAppend = overflow.filter(
        item => !kept.some(s => isSameSymbol(s.symbol, item.symbol))
      );
      const nextBacklog = kept.concat(toAppend);

      form.setFieldValue('symbols', {
        active: nextActive.map(item => ({ ...item, id: JSON.stringify(item.symbol) })),
        backlog: nextBacklog.map(item => ({ ...item, id: JSON.stringify(item.symbol) }))
      });
      form.validate('change');
    },
    [form]
  );

  const removeSymbol = useCallback(
    (symbol: TabSymbol) => {
      const current = form.state.values?.symbols;
      if (!current) return;

      const isInActiveSymbols = current.active.some(s => isEqual(s.symbol, symbol.symbol));

      if (isInActiveSymbols) {
        // Removing from active symbols
        const updatedActiveSymbols = current.active.filter(s => !isEqual(s.symbol, symbol.symbol));

        if (current.backlog.length > 0) {
          // If backlog is not empty, move first backlog item to active
          const [firstBacklogItem, ...restBacklog] = current.backlog;
          form.setFieldValue('symbols', {
            active: [...updatedActiveSymbols, firstBacklogItem],
            backlog: restBacklog
          });
        } else {
          // If backlog is empty, just remove from active
          form.setFieldValue('symbols', {
            active: updatedActiveSymbols,
            backlog: current.backlog
          });
        }
      } else {
        // Removing from backlog
        const newBacklog = current.backlog.filter(s => !isEqual(s.symbol, symbol.symbol));
        form.setFieldValue('symbols', {
          active: current.active,
          backlog: newBacklog
        });
      }
      form.validate('change');
    },
    [form]
  );

  const updateSymbol = useCallback(
    (symbol: TabSymbol) => {
      const current = form.state.values?.symbols;
      if (!current) return;

      const isInActiveSymbols = current.active.some(s => s.id === symbol.id);
      const isInBacklogSymbols = current.backlog.some(s => s.id === symbol.id);

      if (isInActiveSymbols) {
        const updatedActiveSymbols = current.active.map(s => {
          if (s.id === symbol.id) {
            return { ...s, ...symbol };
          }
          return s;
        });
        form.setFieldValue('symbols', {
          active: updatedActiveSymbols,
          backlog: current.backlog
        });
        form.validate('change');
      } else if (isInBacklogSymbols) {
        const updatedBacklogSymbols = current.backlog.map(s => {
          if (s.id === symbol.id) {
            return { ...s, ...symbol };
          }
          return s;
        });
        form.setFieldValue('symbols', {
          active: current.active,
          backlog: updatedBacklogSymbols
        });
        form.validate('change');
      }
    },
    [form]
  );

  const metaValue: AppFormMetaContextValue = useMemo(
    () => ({
      setInitialValues,
      mode,
      setMode,
      resetAppForm,
      maxItems,
      addSymbols,
      removeSymbol,
      updateSymbol,
      canPreview,
      setCanPreview
    }),
    [
      addSymbols,
      canPreview,
      maxItems,
      mode,
      removeSymbol,
      resetAppForm,
      setInitialValues,
      updateSymbol
    ]
  );

  return (
    <formContext.Provider value={form}>
      <AppFormMetaContext value={metaValue}>{children}</AppFormMetaContext>
    </formContext.Provider>
  );
};

function useAppFormMeta() {
  const context = useContext(AppFormMetaContext);
  if (context === undefined) {
    throw new Error('useAppFormMeta must be used within an AppFormProvider');
  }
  return context;
}

/**
 * Hook to access the app form instance with full type safety
 *
 * Returns the form instance from TanStack Form's context along with
 * app-specific metadata (mode, setInitialValues, resetAppForm)
 */
export function useAppForm() {
  const form = useTanStackFormContext() as unknown as AppFormInstance;
  const meta = useAppFormMeta();

  return {
    form,
    ...meta
  };
}
